import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import AppDateRange from '@tenant/core/AppDateRange';
import { AUDIT_ENTITY_TYPES } from 'redux/slices/auditLog/constants/entityTypes';
import { shouldShowAppIdFilter } from 'redux/slices/auditLog/helpers';

const EMPTY_FILTER = {
  entityType: '',
  entityId: '',
  actorUserId: '',
  systemActorOnly: false,
  occurredFrom: null,
  occurredTo: null,
  correlationId: '',
  appId: '',
};

export const AUDIT_LOG_EMPTY_FILTER = EMPTY_FILTER;

// Filter panel for the audit log view (AC-10/AC-11). Plain useState seeded from the redux
// `filter` (list-screen idiom) — not Formik, these are lightweight list filters, not an
// add/edit form. Apply/Reset feed the server query params (buildAuditQueryParams).
const AuditLogFilters = ({ onApply, onReset }) => {
  const { filter } = useSelector((s) => s.auditLog);
  const { permissions } = useSelector((s) => s.authorization);
  const canFilterByApp = shouldShowAppIdFilter(permissions);

  const [draft, setDraft] = useState({ ...EMPTY_FILTER, ...filter });

  const setField = (field, value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const handleApply = () => onApply(draft);
  const handleReset = () => {
    setDraft(EMPTY_FILTER);
    onReset();
  };

  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'flex-start',
      }}
    >
      <FormControl size='small' sx={{ minWidth: 180 }}>
        <InputLabel id='audit-entity-type-label'>Entity type</InputLabel>
        <Select
          labelId='audit-entity-type-label'
          label='Entity type'
          value={draft.entityType ?? ''}
          onChange={(e) => setField('entityType', e.target.value)}
        >
          <MenuItem value=''>All</MenuItem>
          {AUDIT_ENTITY_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size='small'
        label='Entity ID'
        value={draft.entityId ?? ''}
        onChange={(e) => setField('entityId', e.target.value)}
      />

      <TextField
        size='small'
        label='Actor (user id)'
        value={draft.actorUserId ?? ''}
        disabled={!!draft.systemActorOnly}
        onChange={(e) => setField('actorUserId', e.target.value)}
      />

      <FormControlLabel
        sx={{ ml: 0 }}
        control={
          <Checkbox
            checked={!!draft.systemActorOnly}
            onChange={(e) => setField('systemActorOnly', e.target.checked)}
          />
        }
        label='System actor only'
      />

      <AppDateRange
        rangeDate={
          draft.occurredFrom || draft.occurredTo
            ? [
                draft.occurredFrom ? moment(draft.occurredFrom) : moment(),
                draft.occurredTo ? moment(draft.occurredTo) : moment(),
              ]
            : []
        }
        onChange={([from, to]) => {
          setDraft((prev) => ({
            ...prev,
            occurredFrom: from ? from.toISOString() : null,
            occurredTo: to ? to.toISOString() : null,
          }));
        }}
      />

      <TextField
        size='small'
        label='Correlation ID'
        value={draft.correlationId ?? ''}
        onChange={(e) => setField('correlationId', e.target.value)}
      />

      {canFilterByApp && (
        <TextField
          size='small'
          type='number'
          label='App ID'
          value={draft.appId ?? ''}
          onChange={(e) => setField('appId', e.target.value)}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button size='small' variant='contained' onClick={handleApply}>
          Apply filters
        </Button>
        <Button size='small' color='secondary' onClick={handleReset}>
          Reset
        </Button>
      </Box>
    </Box>
  );
};

AuditLogFilters.propTypes = {
  onApply: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

export default React.memo(AuditLogFilters);
